import { IconNames16, ISelectOption, Select } from '@pinpointhq/thumbtack';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import * as React from 'react';

interface IPlaceAutoCompleteProps {
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  selectedOption: ISelectOption | null;
  setSelectedOption: (option: ISelectOption | null) => void;
  placeholder?: string;
  restrictedCountry?: string;
}

export const extractAddressComponents = (place: google.maps.places.PlaceResult, isUnitedStates: boolean) => {
  // Helper functions to get either a long name ("Australia") or short name ("AU")
  const componentLongName = (type: string): string | undefined =>
    place.address_components.find((component) => component.types.includes(type))?.long_name;
  const componentShortName = (type: string): string | undefined =>
    place.address_components.find((component) => component.types.includes(type))?.short_name;

  // USA form renders states + cities. Administrative Level 1 is state and locality is the city
  // Non-USA: Some inner city locations return as postal_town in lieu of a locality field
  const autocompletedAddressOne = [componentLongName('street_number'), componentLongName('route')].join(' ').trim();
  const autocompletedAddressTwo = isUnitedStates ? componentLongName('locality') : ''; // I don't think there's a reasonable way to map between autocompletions and this level of address
  const autocompletedTown = isUnitedStates
    ? componentShortName('administrative_area_level_1')
    : componentLongName('locality') || componentLongName('postal_town');
  const autocompletedPostcode = componentLongName('postal_code');
  const autocompletedCountry = componentShortName('country');

  return {
    autocompletedAddressOne,
    autocompletedAddressTwo,
    autocompletedTown,
    autocompletedPostcode,
    autocompletedCountry,
  };
};

// Based on: https://github.com/visgl/react-google-maps/blob/main/examples/autocomplete/src/autocomplete-custom.tsx
export const PlaceAutoComplete = ({
  onPlaceSelect,
  selectedOption,
  setSelectedOption,
  placeholder,
  restrictedCountry,
}: IPlaceAutoCompleteProps) => {
  const places = useMapsLibrary('places');

  // https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompleteSessionToken
  const [sessionToken, setSessionToken] = React.useState<google.maps.places.AutocompleteSessionToken>();
  // https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service
  const [autocompleteService, setAutocompleteService] = React.useState<google.maps.places.AutocompleteService | null>(
    null,
  );
  // https://developers.google.com/maps/documentation/javascript/reference/places-service
  const [placesService, setPlacesService] = React.useState<google.maps.places.PlacesService | null>(null);
  const [predictionResults, setPredictionResults] = React.useState<Array<google.maps.places.AutocompletePrediction>>(
    [],
  );

  const dummyElement = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    (window as any).placeAutoCompleteOnPlaceSelect = onPlaceSelect;
  }, [onPlaceSelect]);

  React.useEffect(() => {
    if (!places) return;

    setAutocompleteService(new places.AutocompleteService());
    setPlacesService(new places.PlacesService(dummyElement.current));
    setSessionToken(new places.AutocompleteSessionToken());

    return () => setAutocompleteService(null);
  }, [places]);

  const fetchPredictions = React.useCallback(
    async (inputValue: string) => {
      if (!autocompleteService || !inputValue) {
        setPredictionResults([]);
        return;
      }

      const request = {
        input: inputValue,
        sessionToken,
        componentRestrictions: {
          country: restrictedCountry,
        },
      };

      const response = await autocompleteService.getPlacePredictions(request);

      const excludedTypes = new Set(['country', 'administrative_area_level_1', 'locality']);
      const filteredPredictions = response.predictions.filter((prediction) => {
        const { types } = prediction;
        return !types.some((type) => excludedTypes.has(type));
      });

      setPredictionResults(filteredPredictions);
    },
    [autocompleteService, sessionToken, restrictedCountry],
  );

  const onInputChange = React.useCallback(
    (query: string) => {
      fetchPredictions(query);
    },
    [fetchPredictions],
  );

  const handleSuggestionClick = React.useCallback(
    (placeId: string) => {
      if (!places) return;

      const detailRequestOptions = {
        placeId,
        fields: ['geometry', 'formatted_address', 'address_components'],
        sessionToken,
      };

      const detailsRequestCallback = (placeDetails: google.maps.places.PlaceResult | null) => {
        onPlaceSelect(placeDetails);
        setPredictionResults([]);
        setSessionToken(new places.AutocompleteSessionToken());
      };

      placesService?.getDetails(detailRequestOptions, detailsRequestCallback);
    },
    [onPlaceSelect, places, placesService, sessionToken],
  );

  const onItemSelect = (item: ISelectOption | null) => {
    if (!item) {
      setSelectedOption(null);
      onPlaceSelect(null);
      return;
    }

    setSelectedOption(item);
    handleSuggestionClick(item?.value);
  };

  const options: ISelectOption[] = predictionResults.map(({ place_id, description }) => ({
    value: place_id,
    label: description,
    icon: IconNames16.MAP_PIN,
  }));

  return (
    <>
      <div ref={dummyElement} style={{ display: 'none' }} />
      <Select
        // Specific item predicate to show results beyond an exact order text match
        // Instead, split the query string by spaces and commas, and check for matches
        // This allows for more flexible matching, e.g. "parklands 5b" will match "5b Parklands"
        // Necessary as there are sometimes discrepancies between how people write their address and how Google returns them
        itemPredicate={(query: string, item: ISelectOption) => {
          const normalizedLabel = item.label.toLowerCase();
          const normalizedQuery = query.toLowerCase();

          const queryWords = normalizedQuery.split(/[\s,]+/).filter((word) => word.length > 0);
          return queryWords.every((word) => normalizedLabel.includes(word));
        }}
        placeholder={placeholder || 'Start typing an address'}
        fill={true}
        buttonProps={{ large: true }}
        activeItem={selectedOption}
        onQueryChange={onInputChange}
        items={options}
        onItemSelect={onItemSelect}
        clearable={true}
      />
    </>
  );
};
